"use client"

import { useMemo } from "react"
import { usePathname } from "next/navigation"
import { useTranslations } from "next-intl"
import { Compass, Crown, ImageIcon } from "lucide-react"
import { useAuth } from "@/features/auth/hooks/use-auth"
import { cn } from "@/shared/lib/utils"
import { LocalizedLink } from "@/shared/components/i18n/localized-link"
import { stripLocaleFromPath } from "@/shared/lib/i18n/config"
import { isPaidTier, ROUTES } from "@/shared/lib/constants"
import {
  createFilledIcon,
  CreateNavIcon,
  HomeNavIcon,
} from "@/shared/components/icons/inline-nav-icons"
import { useUIStore } from "@/shared/stores/ui-store"
import { resolveMobilePremiumNavAction } from "./mobile-premium-nav"

interface BottomNavItem {
  id: "home" | "explore" | "create" | "generate"
  label: string
  href: string
  icon: React.ComponentType<{ filled?: boolean; className?: string }>
}

// Memoize outside render — these are static factory references.
const ExploreIcon = createFilledIcon(Compass)
const GenerateIcon = createFilledIcon(ImageIcon)
const PremiumIcon = createFilledIcon(Crown)
const navItemClassName =
  "flex h-full min-w-[3rem] flex-col items-center justify-center gap-0.5 border-0 bg-transparent p-0 shadow-none appearance-none"

export function BottomNav() {
  const t = useTranslations("chrome")
  const pathname = usePathname()
  const canonicalPathname = stripLocaleFromPath(pathname)
  const { isAuthenticated, user } = useAuth()
  const { setActiveModal, setAuthContext, openSettings } = useUIStore()
  const isPaid = isPaidTier(user?.subscription ?? "free")
  const premiumAction = resolveMobilePremiumNavAction({ isAuthenticated, isPaid })
  const premiumLabel = t("nav.premium")
  const isPremiumActive =
    canonicalPathname.startsWith(ROUTES.subscription) ||
    (premiumAction.type === "manage" && canonicalPathname.startsWith("/settings"))

  const items: BottomNavItem[] = useMemo(
    () => [
      { id: "home", label: t("nav.home"), href: "/", icon: HomeNavIcon },
      { id: "explore", label: t("nav.explore"), href: "/explore", icon: ExploreIcon },
      { id: "create", label: t("nav.create"), href: "/create", icon: CreateNavIcon },
      { id: "generate", label: t("nav.generate"), href: "/generate/image", icon: GenerateIcon },
    ],
    [t]
  )

  function openPremiumAuth() {
    setAuthContext({
      characterId: null,
      characterName: null,
      characterAvatar: null,
      source: "subscription",
    })
    setActiveModal("login")
  }

  function handlePremiumClick() {
    if (premiumAction.type === "auth") {
      openPremiumAuth()
      return
    }
    if (premiumAction.type === "manage") {
      openSettings("billing", { source: "billing" })
    }
  }

  // Hide on individual chat pages (input area occupies bottom), create page (wizard has its own nav),
  // and generate pages (prompt bar occupies bottom)
  if (
    canonicalPathname.startsWith("/chat/") ||
    canonicalPathname === "/create" ||
    canonicalPathname.startsWith("/generate") ||
    canonicalPathname.startsWith("/explore")
  ) {
    return null
  }

  const premiumContent = (
    <>
      <PremiumIcon
        filled
        className="size-6 text-foreground transition-colors"
      />
      <span className="text-[10px] font-medium leading-none text-foreground">
        {premiumLabel}
      </span>
    </>
  )

  return (
    <nav
      // Sora-spec floating bottom nav: solid black canvas (kept for iOS safe-area
      // overscroll coverage via the box-shadow trick) + 1px hairline top edge
      // so the chrome reads as floating instead of welded to the content.
      className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-black border-t border-border"
      aria-label="Mobile navigation"
      style={{
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        /* Extend background well past the bottom edge to cover any
           gap caused by browser chrome, dynamic toolbars, or
           overscroll on iOS/Android/Chrome/Firefox/Safari */
        boxShadow: "0 100px 0 100px #000000",
      }}
    >
      <div className="flex items-center justify-around h-14 px-1">
        {items.map((item) => {
          const isActive =
            item.href === "/"
              ? canonicalPathname === "/"
              : canonicalPathname.startsWith(item.href)
          const Icon = item.icon

          return (
            <LocalizedLink
              key={item.href}
              href={item.href}
              className={navItemClassName}
              aria-label={item.label}
            >
              <Icon
                filled={isActive}
                className={cn(
                  "size-6 transition-colors",
                  isActive ? "text-foreground" : "text-muted-foreground"
                )}
              />
              <span
                className={cn(
                  "text-[10px] font-medium leading-none",
                  isActive ? "text-foreground" : "text-muted-foreground"
                )}
              >
                {item.label}
              </span>
            </LocalizedLink>
          )
        })}

        {premiumAction.type === "subscribe" ? (
          <LocalizedLink
            href={premiumAction.href}
            className={navItemClassName}
            aria-label={premiumLabel}
            aria-current={isPremiumActive ? "page" : undefined}
          >
            {premiumContent}
          </LocalizedLink>
        ) : (
          <button
            type="button"
            onClick={handlePremiumClick}
            className={navItemClassName}
            aria-label={premiumLabel}
            aria-current={isPremiumActive ? "page" : undefined}
          >
            {premiumContent}
          </button>
        )}
      </div>
    </nav>
  )
}
